import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, Trash2, Clock, ExternalLink, ArrowLeft, Library as LibraryIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useArticleContextMenu } from './ArticleContextMenu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Local storage key for unsigned users
const LOCAL_LIBRARY_KEY = 'yegoNews_savedArticles';

import { getTimeAgo } from '../lib/dateUtils';

// Get saved articles from localStorage
export const getLocalSavedArticles = () => {
  try {
    const saved = localStorage.getItem(LOCAL_LIBRARY_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Helper to get normalized article ID
const getArticleKey = (article) => {
  if (!article) return '';
  if (typeof article === 'object') {
    return String(article.id || article._id || article.link || article.url || article.title || '');
  }
  return String(article);
};

// Save or unsave article to localStorage
export const saveArticleLocally = (article) => {
  if (!article) return false;
  try {
    const saved = getLocalSavedArticles();
    const articleKey = getArticleKey(article);
    if (!articleKey) return false;

    // Check if already saved
    const existingIndex = saved.findIndex(a => getArticleKey(a) === articleKey);
    let isSavedNow = false;

    if (existingIndex >= 0) {
      // Remove if already saved
      saved.splice(existingIndex, 1);
      localStorage.setItem(LOCAL_LIBRARY_KEY, JSON.stringify(saved));
      isSavedNow = false;
    } else {
      // Add to saved with all metadata preserved
      const itemToSave = {
        id: article.id || article._id || articleKey,
        _id: article._id || article.id || articleKey,
        title: article.title || 'Untitled',
        description: article.description || article.summary || '',
        image_url: article.image_url || article.imageUrl || article.urlToImage || '',
        source: typeof article.source === 'string' ? article.source : (article.source?.name || 'YegoNews'),
        category: article.category || 'general',
        link: article.link || article.url || '',
        published_at: article.published_at || article.created_at || article.date || new Date().toISOString(),
        is_user_created: Boolean(article.is_user_created),
        saved_at: new Date().toISOString()
      };
      saved.unshift(itemToSave);
      localStorage.setItem(LOCAL_LIBRARY_KEY, JSON.stringify(saved));
      isSavedNow = true;
    }

    // Broadcast across windows and components
    const detail = { articleId: articleKey, saved: isSavedNow, article };
    window.dispatchEvent(new CustomEvent('articleSaved', { detail }));
    window.dispatchEvent(new CustomEvent('yego_saved_articles_changed', { detail }));

    return isSavedNow;
  } catch (err) {
    console.error('Failed to save article locally:', err);
    return false;
  }
};

// Remove article from localStorage
export const removeLocalArticle = (target) => {
  if (!target) return false;
  try {
    const articleKey = getArticleKey(target);
    const saved = getLocalSavedArticles();
    const filtered = saved.filter(a => getArticleKey(a) !== articleKey);
    localStorage.setItem(LOCAL_LIBRARY_KEY, JSON.stringify(filtered));

    const detail = { articleId: articleKey, saved: false };
    window.dispatchEvent(new CustomEvent('articleSaved', { detail }));
    window.dispatchEvent(new CustomEvent('yego_saved_articles_changed', { detail }));
    return true;
  } catch {
    return false;
  }
};

// Check if article is saved locally
export const isArticleSavedLocally = (target) => {
  if (!target) return false;
  const targetKey = getArticleKey(target);
  if (!targetKey) return false;
  const saved = getLocalSavedArticles();
  return saved.some(a => getArticleKey(a) === targetKey);
};

// Article Card for Library
const LibraryArticleCard = ({ article, onRemove, onArticleClick }) => {
  const { handleContextMenu, handleTouchStart, handleTouchEnd } = useArticleContextMenu();
  const isExternal = !article.is_user_created;

  const handleClick = () => {
    if (onArticleClick) {
      onArticleClick(article);
    } else if (article.link) {
      window.open(article.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      onClick={handleClick}
      onContextMenu={(e) => handleContextMenu(e, article)}
      onTouchStart={(e) => handleTouchStart(e, article)}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className="article-card flex gap-4 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 hover:shadow-lg hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group select-none"
    >
      {/* Thumbnail */}
      <div className="w-32 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
        <img
          src={article.image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200&h=150&fit=crop'}
          alt=""
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200&h=150&fit=crop';
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
            article.is_user_created ? 'bg-emerald-500' : 'bg-blue-500'
          }`}>
            {article.source?.charAt(0) || 'N'}
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{article.source}</span>
          {isExternal && (
            <>
              <span className="text-xs text-gray-400">•</span>
              <ExternalLink className="h-3 w-3 text-blue-500" />
            </>
          )}
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {getTimeAgo(article.published_at || article.created_at || article.date)}
          </span>
        </div>

        <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-emerald-500 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>

        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
          {article.description}
        </p>

        {/* Category + Saved At */}
        <div className="flex items-center justify-between mt-3">
          {article.category && (
            <span className="inline-flex px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full capitalize">
              {article.category.replace('_', ' ')}
            </span>
          )}
          {article.saved_at && (
            <span className="text-xs text-gray-400">
              Saved {getTimeAgo(article.saved_at)}
            </span>
          )}
        </div>
      </div>

      {/* Remove Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(article);
        }}
        className="p-2 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all self-start"
        title="Remove from library"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

const LibraryPage = ({ onBack, onArticleClick, darkMode }) => {
  const { isAuthenticated, user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch saved articles
  const fetchSavedArticles = useCallback(async () => {
    setLoading(true);
    try {
      const localArticles = getLocalSavedArticles();
      let combined = [...localArticles];

      if (isAuthenticated) {
        try {
          const token = localStorage.getItem('yegoNewsToken');
          if (token) {
            const response = await axios.get(`${BACKEND_URL}/api/auth/saved-articles`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const backendArticles = response.data?.articles || response.data || [];
            if (Array.isArray(backendArticles) && backendArticles.length > 0) {
              const localKeys = new Set(combined.map(a => getArticleKey(a)));
              for (const bArt of backendArticles) {
                const bKey = getArticleKey(bArt);
                if (bKey && !localKeys.has(bKey)) {
                  combined.push(bArt);
                  localKeys.add(bKey);
                }
              }
            }
          }
        } catch (err) {
          console.warn('Backend saved-articles sync warning:', err);
        }
      }
      setArticles(combined);
    } catch (error) {
      console.error('Failed to fetch saved articles:', error);
      setArticles(getLocalSavedArticles());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSavedArticles();

    const handleSavedChange = () => {
      fetchSavedArticles();
    };

    window.addEventListener('articleSaved', handleSavedChange);
    window.addEventListener('yego_saved_articles_changed', handleSavedChange);
    window.addEventListener('storage', handleSavedChange);
    return () => {
      window.removeEventListener('articleSaved', handleSavedChange);
      window.removeEventListener('yego_saved_articles_changed', handleSavedChange);
      window.removeEventListener('storage', handleSavedChange);
    };
  }, [fetchSavedArticles]);

  // Handle remove article
  const handleRemove = async (articleOrId) => {
    try {
      const articleKey = getArticleKey(articleOrId);
      // Also remove from local storage
      removeLocalArticle(articleOrId);

      if (isAuthenticated) {
        const token = localStorage.getItem('yegoNewsToken');
        if (token && articleKey) {
          await axios.post(`${BACKEND_URL}/api/auth/unsave-article?article_id=${articleKey}`, { article_id: articleKey }, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      }
      // Update state
      setArticles(prev => prev.filter(a => getArticleKey(a) !== articleKey));
    } catch (error) {
      console.error('Failed to remove article:', error);
    }
  };

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
          <LibraryIcon className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Library</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {articles.length} {articles.length === 1 ? 'article' : 'articles'} saved for later
          </p>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse flex gap-4 p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-32 h-24 bg-gray-200 dark:bg-gray-800 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/4 mb-2" />
                <div className="h-5 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800">
          <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-6">
            <Bookmark className="h-10 w-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Your library is empty
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Save articles to read later by right-clicking on any article card and selecting &quot;Save to Library&quot;
          </p>
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Browse articles
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((article, idx) => (
            <LibraryArticleCard
              key={article.id || idx}
              article={article}
              onRemove={handleRemove}
              onArticleClick={onArticleClick}
            />
          ))}
        </div>
      )}

      {/* Info for unsigned users */}
      {!isAuthenticated && articles.length > 0 && (
        <div className="mt-8 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>Note:</strong> Your saved articles are stored locally in this browser. 
            Sign in to sync your library across devices and never lose your saved articles.
          </p>
        </div>
      )}
    </div>
  );
};

export default LibraryPage;
